import {defineStore} from 'pinia';
import {
  computed,
  ref,
} from 'vue';
import {useAxios} from '@/plugins/Axios/axios';
import {
  GalleryMode,
  useConfigStore,
} from './config';

export interface SessionMedia {
  isMain: boolean,
  printedCopies: number,
  printSize: string,
  name: string,
  filename: string,
  url: string,
}

export interface Photo {
  id: string,
  url: string,
  createdAt: Date,
}

export const usePhotoStore = defineStore('photos', () => {
  const photos = ref<Photo[]>([]);
  const backgroundPhotos = ref<Photo[]>([]); // Frozen after initial load for 3D background
  const eventToken = ref<string | null>(null);
  const isLoading = ref(false);

  // Reactive timestamp that updates periodically to re-evaluate visiblePhotos
  const currentTime = ref(Date.now());

  const approvedPhotos = computed(() =>
    photos.value,
  );

  const visiblePhotos = computed(() => {
    const configStore = useConfigStore();
    const now = new Date(currentTime.value);
    const cutoffTime = new Date(now.getTime() - configStore.delayMinutes * 60 * 1000);

    return photos.value.filter((photo) => photo.createdAt <= cutoffTime);
  });

  function setEventToken(token: string) {
    eventToken.value = token;
  }

  function getEventTokenFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('eventToken') || urlParams.get('event');
  }

  function isValidUrl(value: string): boolean {
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  }

  function getMediaList(session: any): SessionMedia[] {
    const media = session.data?.media;
    if (!media) return [];
    if (Array.isArray(media)) return media;
    return Object.values(media);
  }

  function mapSessionsToPhotos(sessions: any[]): Photo[] {
    const configStore = useConfigStore();
    return sessions
      .filter((session) => {
        // Only show successfully finished sessions
        if (session.data?.general?.state !== 'successfully finished') {
          return false;
        }

        const media = getMediaList(session);

        if (configStore.mode === GalleryMode.RESTRICTED && session.data?.approvals?.isVisible !== true) {
          return;
        }

        if (configStore.mode === GalleryMode.AUTOMATIC && session.data?.approvals?.isVisible === false) {
          return;
        }
        return media.some((m) => m.isMain && m.url && isValidUrl(m.url));
      })
      .map((session) => {
        const media = getMediaList(session);
        const mainMedia = media.find((m) => m.isMain)!;

        // Use ended if available, otherwise started (from session.data.general)
        const sessionTime = session.data?.general?.ended ?? session.data?.general?.started ?? Date.now();

        return {
          id: session.guid ?? session.id ?? String(Date.now()),
          url: mainMedia.url,
          createdAt: new Date(sessionTime),
        };
      });
  }

  async function fetchSessions() {
    if (!eventToken.value) {
      const tokenFromUrl = getEventTokenFromUrl();
      if (tokenFromUrl) {
        eventToken.value = tokenFromUrl;
      } else {
        console.error('Event token not found in URL. Add ?eventToken=YOUR_TOKEN to the URL.');
        return;
      }
    }

    isLoading.value = true;
    try {
      const axios = useAxios();
      const {data} = await axios.get(`/api/v1/external/event/${eventToken.value}/session`);

      const mappedPhotos = mapSessionsToPhotos(Array.isArray(data) ? data : data.data ?? []);
      photos.value = mappedPhotos;
      // Set background photos only on initial load (freeze them)
      if (backgroundPhotos.value.length === 0) {
        backgroundPhotos.value = [...mappedPhotos];
      }
      // Update current time to re-evaluate visiblePhotos
      currentTime.value = Date.now();
    } catch (e) {
      console.error('Failed to fetch sessions:', e);
    } finally {
      isLoading.value = false;
    }
  }

  // Background refetch without loading state
  async function refetchSessions() {
    if (!eventToken.value) return;

    try {
      const axios = useAxios();
      const {data} = await axios.get(`/api/v1/external/event/${eventToken.value}/session`);

      photos.value = mapSessionsToPhotos(Array.isArray(data) ? data : data.data ?? []);
      // Update current time to re-evaluate visiblePhotos
      currentTime.value = Date.now();
    } catch (e) {
      console.error('Failed to refetch sessions:', e);
    }
  }

  let refetchInterval: ReturnType<typeof setInterval> | null = null;

  function startAutoRefetch(intervalSeconds = 60) {
    stopAutoRefetch();
    refetchInterval = setInterval(() => {
      refetchSessions();
    }, intervalSeconds * 1000);
  }

  function stopAutoRefetch() {
    if (refetchInterval !== null) {
      clearInterval(refetchInterval);
      refetchInterval = null;
    }
  }

  return {
    photos,
    backgroundPhotos,
    eventToken,
    isLoading,
    approvedPhotos,
    visiblePhotos,
    setEventToken,
    getEventTokenFromUrl,
    fetchSessions,
    refetchSessions,
    startAutoRefetch,
    stopAutoRefetch,
  };
});
